import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    User,
    Mail,
    Building2,
    Phone,
    Calendar,
    Lock,
    Eye,
    EyeOff,
    Loader2,
    Save,
    LogOut,
    AlertTriangle,
    Trash2,
    Sun,
    Moon,
    Rows2,
    Users,
    UserPlus,
    LogOut as LeaveIcon,
    Crown,
    X,
} from "lucide-react";
import TopNav from "../components/TopNav";
import { AvatarUpload } from "../components/Avatar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth, formatApiError } from "../lib/auth";
import {
    api,
    disableMyAccount,
    listWorkgroups,
    listWorkgroupMembers,
    createWorkgroup,
    sendWorkgroupInvite,
    listWorkgroupInvites,
    cancelWorkgroupInvite,
    removeWorkgroupMember,
    deleteWorkgroup,
} from "../lib/api";

export default function AccountPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleSignOut = () => {
        logout();
        navigate("/", { replace: true });
    };

    if (!user) return null;

    return (
        <div className="h-[100dvh] overflow-hidden blueprint-bg text-slate-100 flex flex-col">
            <TopNav active="account" onTabChange={() => navigate("/home")} />

            <main className="flex-1 min-h-0 overflow-y-auto px-4 md:px-8 pt-4 pb-24">
                <div className="max-w-3xl mx-auto">
                    <button
                        onClick={() => navigate("/home")}
                        data-testid="btn-account-back"
                        className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to dashboard
                    </button>

                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white mb-1">
                        Account
                    </h1>
                    <p className="text-sm text-slate-400 mb-6">
                        Manage your profile and security settings.
                    </p>

                    <ProfileCard user={user} />
                    <CompanyProfileCard user={user} />
                    <ThemePrefCard user={user} />
                    <CompactModePrefCard user={user} />
                    <ChangePasswordCard user={user} />
                    <DangerZoneCard user={user} onSignedOut={handleSignOut} />

                    <div className="mt-6">
                        <button
                            onClick={handleSignOut}
                            data-testid="btn-account-signout"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-slate-500/30 bg-slate-500/10 text-slate-300 hover:bg-slate-500/20 text-sm font-semibold"
                        >
                            <LogOut className="w-4 h-4" />
                            Sign out of Steeltooth
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
}

function ThemePrefCard({ user }) {
    const { updatePreferences } = useAuth();
    // iter-131e: Theme temporarily LOCKED to light for everyone.
    // We keep the toggle visible (per user request) but disable it so
    // no one can flip it. Flip `THEME_LOCKED` to `false` later to
    // re-enable user choice without touching the rest of this file.
    const THEME_LOCKED = true;
    const light = THEME_LOCKED ? true : user?.preferences?.theme_light_mode !== false;
    const [saving, setSaving] = useState(false);

    const flip = async () => {
        if (THEME_LOCKED) {
            toast.info("Light mode is on for everyone right now. Dark mode is coming back soon.");
            return;
        }
        setSaving(true);
        try {
            await updatePreferences({ theme_light_mode: !light });
            toast.success(!light ? "Light mode on" : "Dark mode on");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6 mb-5"
            data-testid="account-theme-card"
        >
            <div className="flex items-center gap-3 mb-3">
                {light ? (
                    <Sun className="w-5 h-5 text-amber-400" />
                ) : (
                    <Moon className="w-5 h-5 text-[#00A3FF]" />
                )}
                <div className="min-w-0 flex-1">
                    <h2 className="text-white text-base font-bold">Appearance</h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                        Your personal theme choice. Saved to your account so it follows you across devices.
                    </p>
                </div>
            </div>
            <label
                className="flex items-center gap-3 px-3 py-3 bg-[#0A1128] border border-[#2A3655] rounded-sm cursor-pointer hover:border-[#00A3FF]/60 transition-colors"
                data-testid="account-theme-row"
            >
                {light ? (
                    <Sun className="w-4 h-4 text-amber-400 shrink-0" />
                ) : (
                    <Moon className="w-4 h-4 text-[#00A3FF] shrink-0" />
                )}
                <span className="flex-1 text-sm text-white min-w-0">
                    Light mode
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        {light ? "on" : "off"}
                    </span>
                </span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={light}
                    onClick={flip}
                    disabled={saving}
                    data-testid="account-theme-toggle"
                    className={`relative inline-flex shrink-0 h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${
                        light ? "bg-amber-500" : "bg-[#2A3655]"
                    }`}
                >
                    <span
                        className={`absolute top-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            light ? "translate-x-5" : "translate-x-0.5"
                        }`}
                    />
                    {saving && (
                        <Loader2 className="w-3 h-3 absolute -right-5 top-1.5 animate-spin text-slate-400" />
                    )}
                </button>
            </label>
        </section>
    );
}

function CompactModePrefCard({ user }) {
    const { updatePreferences } = useAuth();
    const compact = Boolean(user?.preferences?.compact_mode);
    const [saving, setSaving] = useState(false);

    // iter-178: One-tap compact toggle. Adds `mode-compact` to the
    // <html> element via ThemeApplier and lets a global CSS block
    // shrink padding on every dialog / sheet / picker — no per-
    // component changes needed. Great for tablet field crews.
    const flip = async () => {
        setSaving(true);
        try {
            await updatePreferences({ compact_mode: !compact });
            toast.success(!compact ? "Compact mode on" : "Compact mode off");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6 mb-5"
            data-testid="account-compact-card"
        >
            <div className="flex items-center gap-3 mb-3">
                <Rows2 className="w-5 h-5 text-emerald-400" />
                <div className="min-w-0 flex-1">
                    <h2 className="text-white text-base font-bold">Compact mode</h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                        Shrinks padding across every dialog and side-sheet so
                        field crews on tablets fit more content per screen.
                    </p>
                </div>
            </div>
            <label
                className="flex items-center gap-3 px-3 py-3 bg-[#0A1128] border border-[#2A3655] rounded-sm cursor-pointer hover:border-[#00A3FF]/60 transition-colors"
                data-testid="account-compact-row"
            >
                <Rows2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="flex-1 text-sm text-white min-w-0">
                    Denser layout
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        {compact ? "on" : "off"}
                    </span>
                </span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={compact}
                    onClick={flip}
                    disabled={saving}
                    data-testid="account-compact-toggle"
                    className={`relative inline-flex shrink-0 h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${
                        compact ? "bg-emerald-500" : "bg-[#2A3655]"
                    }`}
                >
                    <span
                        className={`absolute top-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            compact ? "translate-x-5" : "translate-x-0.5"
                        }`}
                    />
                    {saving && (
                        <Loader2 className="w-3 h-3 absolute -right-5 top-1.5 animate-spin text-slate-400" />
                    )}
                </button>
            </label>
        </section>
    );
}

function WorkgroupCard({ user }) {
    const [workgroup, setWorkgroup] = React.useState(null);
    const [members, setMembers] = React.useState([]);
    const [invites, setInvites] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [inviteEmail, setInviteEmail] = React.useState("");
    const [inviting, setInviting] = React.useState(false);
    const [newName, setNewName] = React.useState("");
    const [creating, setCreating] = React.useState(false);
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const wgs = await listWorkgroups();
            if (wgs && wgs.length > 0) {
                const wg = wgs[0];
                setWorkgroup(wg);
                const [mems, invs] = await Promise.all([
                    listWorkgroupMembers(wg.id).catch(() => []),
                    listWorkgroupInvites(wg.id).catch(() => []),
                ]);
                setMembers(mems || []);
                setInvites((invs || []).filter((i) => i.status === "pending"));
            } else {
                setWorkgroup(null);
                setMembers([]);
                setInvites([]);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    React.useEffect(() => {
        load();
    }, [load]);

    const isOwner = workgroup && workgroup.created_by_user_id === user?.user_id;

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        try {
            await createWorkgroup(newName.trim());
            toast.success("Workgroup created — your existing projects, contacts, leads, and reports are now shared with any teammate you invite.");
            setNewName("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCreating(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        const email = inviteEmail.trim();
        if (!email || !email.includes("@")) {
            toast.error("Enter a valid email.");
            return;
        }
        setInviting(true);
        try {
            await sendWorkgroupInvite(workgroup.id, email);
            toast.success(`Invite sent to ${email}`);
            setInviteEmail("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setInviting(false);
        }
    };

    const handleCancelInvite = async (inviteId) => {
        if (!window.confirm("Cancel this invite?")) return;
        setBusy(true);
        try {
            await cancelWorkgroupInvite(workgroup.id, inviteId);
            toast.success("Invite cancelled.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleKick = async (memberId, memberName) => {
        if (!window.confirm(`Remove ${memberName || "this member"}? Everything they own inside the workgroup will be transferred to you (the owner). They'll start over with an empty account.`)) return;
        setBusy(true);
        try {
            await removeWorkgroupMember(workgroup.id, memberId);
            toast.success(`${memberName || "Member"} removed.`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleLeave = async () => {
        const myMember = members.find((m) => m.user_id === user.user_id);
        if (!myMember) return;
        if (!window.confirm("Leave this workgroup? EVERYTHING you contributed will stay with the workgroup owner — you'll start fresh with an empty account. This can't be undone.")) return;
        setBusy(true);
        try {
            await removeWorkgroupMember(workgroup.id, myMember.id);
            toast.success("You left the workgroup.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete the entire workgroup? Every member will be kicked out and lose access to the shared items. This can't be undone.")) return;
        setBusy(true);
        try {
            await deleteWorkgroup(workgroup.id);
            toast.success("Workgroup deleted.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <section className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6 mb-5">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading workgroup…
                </div>
            </section>
        );
    }

    return (
        <section
            className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6 mb-5"
            data-testid="account-workgroup-card"
        >
            <div className="flex items-center gap-3 mb-3">
                <Users className="w-5 h-5 text-[#00A3FF]" />
                <div className="min-w-0 flex-1">
                    <h2 className="text-white text-base font-bold">
                        {workgroup ? workgroup.name : "Workgroup"}
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                        {workgroup
                            ? "Everything you own is auto-shared with every member. Invite teammates and they'll instantly see all your projects, contacts, leads, and reports — with full edit access."
                            : "Create a workgroup to share every project, contact, lead, and report with teammates automatically. You can only belong to ONE workgroup at a time."}
                    </p>
                </div>
            </div>

            {!workgroup ? (
                <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2 mt-4">
                    <Input
                        placeholder="Workgroup name (e.g. Acme Contracting)"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="flex-1 bg-[#0A1128] border-[#2A3655] text-white"
                        data-testid="wg-create-name"
                    />
                    <Button
                        type="submit"
                        disabled={creating || !newName.trim()}
                        className="bg-[#00A3FF] hover:bg-[#0088dd]"
                        data-testid="wg-create-submit"
                    >
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
                    </Button>
                </form>
            ) : (
                <div className="space-y-5 mt-4">
                    {/* Members list */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
                            Members ({members.length})
                        </div>
                        <div className="rounded-sm border border-[#2A3655] bg-[#0A1128] divide-y divide-[#2A3655]">
                            {members.map((m) => {
                                const isCreator = m.user_id === workgroup.created_by_user_id;
                                const isMe = m.user_id === user.user_id;
                                return (
                                    <div
                                        key={m.id}
                                        className="flex items-center gap-3 px-3 py-2.5"
                                        data-testid={`wg-member-${m.user_id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-white truncate flex items-center gap-2">
                                                {m.name || m.email || m.user_id}
                                                {isMe && <span className="text-[10px] text-slate-500">(you)</span>}
                                                {isCreator && (
                                                    <Crown className="w-3 h-3 text-amber-400" title="Owner" />
                                                )}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                                        </div>
                                        {isOwner && !isCreator && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleKick(m.id, m.name || m.email)}
                                                disabled={busy}
                                                className="h-7 px-2 text-[11px] text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                                                data-testid={`wg-kick-${m.user_id}`}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Invite (owner-only) */}
                    {isOwner && (
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
                                Invite a teammate
                            </div>
                            <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-2">
                                <Input
                                    type="email"
                                    placeholder="teammate@company.com"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    className="flex-1 bg-[#0A1128] border-[#2A3655] text-white"
                                    data-testid="wg-invite-email"
                                />
                                <Button
                                    type="submit"
                                    disabled={inviting || !inviteEmail.trim()}
                                    className="bg-[#00A3FF] hover:bg-[#0088dd]"
                                    data-testid="wg-invite-submit"
                                >
                                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Invite</>}
                                </Button>
                            </form>
                            {invites.length > 0 && (
                                <div className="mt-3 space-y-1">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                        Pending
                                    </div>
                                    {invites.map((inv) => (
                                        <div
                                            key={inv.id}
                                            className="flex items-center gap-2 px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-xs"
                                        >
                                            <span className="flex-1 text-slate-300 truncate">
                                                {inv.invited_email}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleCancelInvite(inv.id)}
                                                disabled={busy}
                                                className="text-slate-500 hover:text-rose-400"
                                                data-testid={`wg-cancel-invite-${inv.id}`}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Danger zone */}
                    <div className="pt-3 border-t border-[#2A3655] flex flex-wrap gap-2">
                        {isOwner ? (
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={busy}
                                className="text-xs"
                                data-testid="wg-delete"
                            >
                                <Trash2 className="w-3 h-3 mr-1" /> Delete workgroup
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={handleLeave}
                                disabled={busy}
                                className="text-xs"
                                data-testid="wg-leave"
                            >
                                <LeaveIcon className="w-3 h-3 mr-1" /> Leave workgroup
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

/**
 * iter-169i — Company profile card.
 *
 * Two side-by-side sub-forms (Installer + Designer) that write to the
 * user doc's `installer_info` / `designer_info` sub-docs. Schedule
 * 1/2/3 auto-fill reads from these sub-docs so users only fill this
 * once instead of every project.
 *
 * Uses the same styling grammar as ProfileCard so it drops in cleanly.
 */
const _COMPANY_FIELDS = [
    { key: "name", label: "Full name" },
    { key: "bcin", label: "BCIN" },
    { key: "firm", label: "Firm (optional)" },
    { key: "street", label: "Street address" },
    { key: "city", label: "City" },
    { key: "province", label: "Province" },
    { key: "postal", label: "Postal code" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "cell", label: "Cell (optional)" },
];

function CompanyProfileCard({ user }) {
    const { updateCompanyProfile } = useAuth();
    const [installer, setInstaller] = useState(() => ({ ...(user?.installer_info || {}) }));
    const [designer, setDesigner] = useState(() => ({ ...(user?.designer_info || {}) }));
    const [saving, setSaving] = useState(false);

    const isDirty = React.useMemo(() => {
        const cur = user?.installer_info || {};
        const cud = user?.designer_info || {};
        for (const f of _COMPANY_FIELDS) {
            if ((installer[f.key] || "") !== (cur[f.key] || "")) return true;
            if ((designer[f.key] || "") !== (cud[f.key] || "")) return true;
        }
        return false;
    }, [installer, designer, user]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateCompanyProfile({
                installer_info: installer,
                designer_info: designer,
            });
            toast.success("Company profile saved");
        } catch (err) {
            toast.error(formatApiError(err) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const copyInstallerToDesigner = () => {
        setDesigner({ ...installer });
        toast.success("Copied installer info into designer");
    };

    const renderSubform = (label, values, setValues) => (
        <div className="space-y-3">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {_COMPANY_FIELDS.map((f) => (
                    <div key={f.key} className="space-y-1">
                        <Label htmlFor={`${label}-${f.key}`} className="text-slate-300 text-xs">{f.label}</Label>
                        <Input
                            id={`${label}-${f.key}`}
                            data-testid={`input-company-${label.toLowerCase()}-${f.key}`}
                            value={values[f.key] || ""}
                            onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                            className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                        />
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <section className="rounded-lg border border-[#2A3655] bg-[#101a35] p-6 mb-5">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h2 className="text-base font-bold text-white">Company profile</h2>
                    <p className="text-xs text-slate-400 mt-1">
                        Auto-fills Schedule 1 (Designer) and Schedule 2 (Installer) sections on every new project.
                    </p>
                </div>
                <button
                    onClick={copyInstallerToDesigner}
                    className="text-xs text-[#007AFF] hover:underline"
                    data-testid="btn-copy-installer-to-designer"
                >
                    Copy installer → designer
                </button>
            </div>

            <div className="space-y-6">
                {renderSubform("Installer", installer, setInstaller)}
                <div className="h-px bg-[#2A3655]" />
                {renderSubform("Designer", designer, setDesigner)}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
                <Button
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                    data-testid="btn-save-company-profile"
                    className="bg-[#007AFF] hover:bg-[#0066CC] text-white disabled:opacity-40"
                >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save profile
                </Button>
            </div>
        </section>
    );
}


function ProfileCard({ user }) {
    const { updateProfilePicture } = useAuth();
    const [saving, setSaving] = useState(false);
    const rows = [
        { icon: User, label: "Full name", value: user.full_name || "—" },
        { icon: Mail, label: "Email", value: user.email },
        { icon: Building2, label: "Company", value: user.company || "—" },
        { icon: Phone, label: "Phone", value: user.phone || "—" },
        { icon: Calendar, label: "Birthdate", value: user.birthdate || "—" },
    ];

    const handlePick = async (dataUrl) => {
        setSaving(true);
        try {
            await updateProfilePicture(dataUrl);
            toast.success("Profile picture updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        setSaving(true);
        try {
            await updateProfilePicture("");
            toast.success("Profile picture removed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6 mb-5"
            data-testid="account-profile-card"
        >
            <div className="flex items-center gap-4 mb-4">
                <AvatarUpload
                    user={user}
                    onPick={handlePick}
                    sizeClass="w-16 h-16"
                    testId="account-avatar-upload"
                />
                <div className="min-w-0 flex-1">
                    <div className="text-white text-lg font-bold truncate">
                        {user.full_name || user.email}
                    </div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-0.5">
                        {user.auth_provider === "google"
                            ? "Google account"
                            : "Email & password"}
                    </div>
                    {user.picture && (
                        <button
                            type="button"
                            onClick={handleRemove}
                            disabled={saving}
                            data-testid="btn-remove-avatar"
                            className="mt-1.5 text-xs text-slate-400 hover:text-red-300 underline-offset-2 hover:underline disabled:opacity-50"
                        >
                            Remove picture
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {rows.map(({ icon: Icon, label, value }) => (
                    <div
                        key={label}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-sm bg-[#0A1128]/60 border border-[#2A3655]"
                    >
                        <Icon className="w-4 h-4 text-[#00A3FF] mt-0.5 shrink-0" />
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                {label}
                            </div>
                            <div className="text-sm text-white truncate">{value}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ChangePasswordCard({ user }) {
    const isGoogle = user.auth_provider === "google";
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const valid =
        current.length > 0 && next.length >= 8 && next === confirm && next !== current;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!valid) {
            if (next.length < 8)
                setError("New password must be at least 8 characters");
            else if (next !== confirm) setError("Passwords don't match");
            else if (next === current)
                setError("New password must differ from current password");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6"
            data-testid="account-change-password-card"
        >
            <div className="flex items-center gap-2 mb-1">
                <Lock className="w-4 h-4 text-[#00A3FF]" />
                <h2 className="text-white font-bold text-lg">Change password</h2>
            </div>
            <p className="text-xs text-slate-400 mb-4">
                Your password is hashed with bcrypt and never stored in plain text.
            </p>

            {isGoogle ? (
                <div
                    data-testid="change-password-google-notice"
                    className="text-sm text-slate-300 bg-[#0A1128]/60 border border-[#2A3655] rounded-sm px-3 py-3"
                >
                    This account signs in with Google, so there's no password to change
                    here. Manage your password through your Google account.
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                    <PwField
                        id="current"
                        label="Current password"
                        value={current}
                        onChange={setCurrent}
                        show={showPw}
                        onToggle={() => setShowPw((s) => !s)}
                        autoComplete="current-password"
                    />
                    <PwField
                        id="new"
                        label="New password (min 8)"
                        value={next}
                        onChange={setNext}
                        show={showPw}
                        onToggle={() => setShowPw((s) => !s)}
                        autoComplete="new-password"
                    />
                    <PwField
                        id="confirm"
                        label="Confirm new password"
                        value={confirm}
                        onChange={setConfirm}
                        show={showPw}
                        onToggle={() => setShowPw((s) => !s)}
                        autoComplete="new-password"
                    />

                    {error && (
                        <div
                            data-testid="change-password-error"
                            className="text-xs font-semibold text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2"
                        >
                            {error}
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={!valid || submitting}
                        data-testid="btn-change-password"
                        className="bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold h-11 disabled:opacity-40 disabled:bg-[#2A3655] disabled:text-slate-400"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Updating…
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4 mr-2" />
                                Update password
                            </>
                        )}
                    </Button>
                </form>
            )}
        </section>
    );
}

function PwField({ id, label, value, onChange, show, onToggle, autoComplete }) {
    return (
        <div className="space-y-1.5">
            <Label
                htmlFor={id}
                className="text-slate-300 text-xs uppercase tracking-wider"
            >
                {label}
            </Label>
            <div className="relative">
                <Input
                    id={id}
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    data-testid={`input-pw-${id}`}
                    className="bg-[#0A1128]/70 border-[#2A3655] text-white focus-visible:ring-[#007AFF] pr-10 h-11"
                />
                <button
                    type="button"
                    onClick={onToggle}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-sm flex items-center justify-center text-slate-400 hover:text-white"
                >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
}


function DangerZoneCard({ user, onSignedOut }) {
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const isPrivileged = ["superadmin", "reviewer"].includes(user?.role);

    const handleDisable = async () => {
        setSubmitting(true);
        try {
            const result = await disableMyAccount();
            const days = result?.grace_days || 30;
            toast.success(
                `Account disabled. Permanently deleted in ${days} days unless you reactivate via the email link.`,
                { duration: 7000 },
            );
            onSignedOut();
        } catch (err) {
            toast.error(formatApiError(err));
            setSubmitting(false);
        }
    };

    return (
        <section
            data-testid="account-danger-zone"
            className="rounded-lg border border-red-500/30 bg-red-500/5 p-5 md:p-6 mt-5"
        >
            <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <h2 className="text-white font-bold text-lg">Danger zone</h2>
            </div>
            <p className="text-xs text-slate-400 mb-4">
                Disabling your account hides it for a grace period (set by the
                admin). During that window you can fully restore it via the
                reactivation link we'll email you. After the window expires,
                everything is <strong>permanently deleted</strong>.
            </p>

            {isPrivileged ? (
                <div
                    data-testid="account-disable-privileged-notice"
                    className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/40 rounded-sm px-3 py-2"
                >
                    Superadmin / reviewer accounts can't self-disable. Have another
                    superadmin do it from Admin → Users.
                </div>
            ) : !confirming ? (
                <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    data-testid="btn-account-disable"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 text-sm font-semibold"
                >
                    <Trash2 className="w-4 h-4" />
                    Disable my account
                </button>
            ) : (
                <div
                    data-testid="account-disable-confirm-box"
                    className="space-y-3"
                >
                    <div className="text-sm text-white bg-[#0A1128]/60 border border-red-500/30 rounded-sm px-3 py-3">
                        Are you sure? You'll be signed out immediately. We'll email
                        you a reactivation link so you can come back during the
                        grace window — after that your account, projects, templates,
                        estimates, and credits are gone.
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleDisable}
                            disabled={submitting}
                            data-testid="btn-account-disable-confirm"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-red-500 hover:bg-red-400 text-white text-sm font-bold disabled:opacity-50"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4" />
                            )}
                            Yes, disable my account
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirming(false)}
                            disabled={submitting}
                            data-testid="btn-account-disable-cancel"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-slate-500/30 text-slate-300 hover:bg-slate-500/10 text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
